// Copyright (c) Microsoft. All rights reserved.

import * as signalR from '@microsoft/signalr';
import { COPY } from '../../../assets/strings';
import { AlertType } from '../../../libs/models/AlertType';
import {
  AgentResponseUpdate,
  ChatMessage,
  IChatMessage,
  IChatStreamingProgressMessage,
} from '../../../libs/models/conversations/ChatMessage';
import { IChatUser } from '../../../libs/models/users/ChatUser';
import { BackendServiceUrl } from '../../../libs/services/BaseService';
import { StoreMiddlewareAPI } from '../../app/store';
import { showAlertToast, setMaintenance } from '../app/appSlice';
import { IChatRoom } from '../../../libs/models/conversations/ChatRoom';
import { UpdateBotStatePayload, UpdatePluginStatePayload } from '../../../libs/models/conversations/ConversationsState';
// import { AuthHelper } from '../../../libs/auth/AuthHelper';
import { AuthHelper } from '../../../libs/auth/AuthHelper';
import { IUsage } from '../../../libs/models/app/AIModel';
import { IMCPRequest } from '../../../libs/models/app/mcp/Tool';
import { getFriendlyChatName } from '../../../libs/hooks/useChat_v2';

/*
 * This is a module that encapsulates the SignalR connection
 * to the messageRelayHub on the server.
 */

// These have to match the callback names used in the backend
const enum SignalRCallbackMethods {
  ReceiveMessage = 'ReceiveMessage',
  ReceiveMessages = 'ReceiveMessages',
  ReceiveMessageUpdate = 'ReceiveMessageUpdate',
  UserJoined = 'UserJoined',
  ReceiveUserTypingState = 'ReceiveUserTypingState',
  ReceiveBotResponseStatus = 'ReceiveBotResponseStatus',
  GlobalDocumentUploaded = 'GlobalDocumentUploaded',
  ChatEdited = 'ChatEdited',
  ChatDeleted = 'ChatDeleted',
  GlobalSiteMaintenance = 'GlobalSiteMaintenance',
  PluginStateChanged = 'PluginStateChanged',
  ReceiveStreamingMessage = 'ReceiveStreamingMessage',
  ReceiveChatStatus = 'ReceiveChatStatus',
  ReceiveChatRoomUpdated = 'ReceiveChatRoomUpdated',
  ReceiveChatRoomJoined = 'ReceiveChatRoomjoined',
  ReceiveChatRoomLeaved = 'ReceiveChatRoomleaved',
  ReceiveRegenerateMessageRequested = 'ReceiveRegenerateMessageRequested',
  ReceiveUsageUpdated = 'ReceiveUsageUpdated',
  ReceiveStreamingProgressMessage = 'ReceiveStreamingProgressMessage',
  InvokeCallTool = 'invokeCallTool',
  InvokeToolResult = 'InvokeToolResult'
}

// Set up a SignalR connection to the messageRelayHub on the server
const setupSignalRConnectionToChatHub = () => {
  const connectionHubUrl = new URL('/chathub', BackendServiceUrl);
  const signalRConnectionOptions = {
    accessTokenFactory: async () => {
      const token = await AuthHelper.getToken();
      return token;
    },
    skipNegotiation: true,
    transport: signalR.HttpTransportType.WebSockets,
    logger: signalR.LogLevel.Warning,
  };

  // Create the connection instance
  // withAutomaticReconnect will automatically try to reconnect and generate a new socket connection if needed
  const hubConnection = new signalR.HubConnectionBuilder()
    .withUrl(connectionHubUrl.toString(), signalRConnectionOptions)
    .withAutomaticReconnect()
    .withHubProtocol(new signalR.JsonHubProtocol())
    .configureLogging(signalR.LogLevel.Information)
    .build();

  // Note: to keep the connection open the serverTimeout should be
  // larger than the KeepAlive value that is set on the server
  // keepAliveIntervalInMilliseconds default is 15000 and we are using default
  // serverTimeoutInMilliseconds default is 30000 and we are using 60000 set below
  hubConnection.serverTimeoutInMilliseconds = 60000;

  return hubConnection;
};

const registerCommonSignalConnectionEvents = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
  // Re-establish the connection if connection dropped
  hubConnection.onclose((error) => {
    if (hubConnection.state === signalR.HubConnectionState.Disconnected) {
      const errorMessage = '연결이 끊어졌습니다. 새로고침을 해주세요.';
      store.dispatch(
        showAlertToast({
          altMessage: String(errorMessage),
          altType: AlertType.Info,
          // id: Constants.app.CONNECTION_ALERT_ID,
        }),
      );
      console.log(errorMessage, error);
    }
  });

  hubConnection.onreconnecting((error) => {
    if (hubConnection.state === signalR.HubConnectionState.Reconnecting) {
      const errorMessage = '재연결을 시도 중입니다.';
      store.dispatch(
        showAlertToast({
          altMessage: String(errorMessage),
          altType: AlertType.Info,
          // id: Constants.app.CONNECTION_ALERT_ID,
        }),
      );
      console.log(errorMessage, error);
    }
  });

  hubConnection.onreconnected((connectionId = '') => {
    if (hubConnection.state === signalR.HubConnectionState.Connected) {
      const message = '재연결이 완료되었습니다. 페이지 새로고침을 하여 최신 데이터를 확인해주세요.';
      store.dispatch(
        // showAlertToast({ altMessage: message, altType: AlertType.Success, id: Constants.app.CONNECTION_ALERT_ID }),
        showAlertToast({ altMessage: message, altType: AlertType.Info }),
      );
      console.log(message + ` Connected with connectionId ${connectionId}`);
    }
  });
};

const startSignalRConnection = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
  registerCommonSignalConnectionEvents(hubConnection, store);
  hubConnection
    .start()
    .then(() => {
      console.assert(hubConnection.state === signalR.HubConnectionState.Connected);
      console.log('SignalR connection established');
    })
    .catch((err) => {
      console.assert(hubConnection.state === signalR.HubConnectionState.Disconnected);
      console.error('SignalR Connection Error: ', err);
      setTimeout(() => {
        startSignalRConnection(hubConnection, store);
      }, 5000);
    });
};

const registerSignalREvents = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
  console.log('registerSignalREvents');

  hubConnection.on(SignalRCallbackMethods.ReceiveMessages, (messages: ChatMessage[]) => {
    console.log('ReceiveMessage', messages);
    // if(message.responseFrom) {
    //   const model = message.messages[message.messages.length-1].model;
    //   store.dispatch({ type: 'model/setSelectedModel', payload: model});
    // }
    store.dispatch({ type: 'conversations/v2/addMessageToConversation', payload: { messages } });
  });

    hubConnection.on(SignalRCallbackMethods.ReceiveMessage, (message: ChatMessage) => {
    store.dispatch({ type: 'conversations/v2/addMessageToConversation', payload: { message } });
  });

  hubConnection.on(SignalRCallbackMethods.ReceiveStreamingMessage, (chatRoomId: string, message: AgentResponseUpdate) => {
    store.dispatch({ type: 'conversations/v2/addStreamingMessageToConversationFromServer', payload: { chatRoomId, message } });
  });

  hubConnection.on(SignalRCallbackMethods.ReceiveMessageUpdate, (message: IChatMessage) => {
    const { chatRoomId, id: messageId } = message;
    // console.log('ReceiveMessageUpdate', message);

    // If tokenUsage is defined, that means full message content has already been streamed and updated from server. No need to update content again.
    store.dispatch({
      type: 'conversations/updateMessageProperty',
      payload: {
        chatRoomId,
        messageIdOrIndex: messageId,
        // property: message.tokenUsage ? 'tokenUsage' : 'content',
        // value: message.tokenUsage || content,
        frontLoad: true,
      },
    });
  });

  hubConnection.on(SignalRCallbackMethods.UserJoined, (chatId: string, userId: string) => {
    const user: IChatUser = {
      id: userId,
      online: false,
      fullName: '',
      emailAddress: '',
      isTyping: false,
      photo: '',
    };
    store.dispatch({ type: 'conversations/addUserToConversation', payload: { user, chatId } });
  });

  hubConnection.on(
    SignalRCallbackMethods.ReceiveUserTypingState,
    (chatId: string, userId: string, isTyping: boolean) => {
      store.dispatch({
        type: 'conversations/updateUserIsTypingFromServer',
        payload: { chatId, userId, isTyping },
      });
    },
  );

  hubConnection.on(SignalRCallbackMethods.ReceiveBotResponseStatus, (chatId: string, status: string) => {
    // console.log('ReceiveBotResponseStatus', status);

    store.dispatch({ type: 'conversations/updateBotResponseStatus', payload: { chatId, status } });
  });

  hubConnection.on(SignalRCallbackMethods.GlobalDocumentUploaded, (fileNames: string, userName: string) => {
    store.dispatch(
      showAlertToast({ altMessage: `${userName}님이 ${fileNames}을 업로드 했어요.`, altType: AlertType.Info }),
    );
  });

  hubConnection.on(SignalRCallbackMethods.ChatEdited, (chat: IChatRoom) => {
    const { id, title } = chat;
    store.dispatch({ type: 'conversations/editConversationTitle', payload: { id, newTitle: title } });
  });

  // User Id is that of the user who initiated the deletion.
  hubConnection.on(SignalRCallbackMethods.ChatDeleted, (chatId: string, userId: string) => {
    const conversations = store.getState().conversations_v2.conversations;
    if (!(chatId in conversations)) {
      store.dispatch({
        message: `${chatId} 채팅방 삭제를 하지 못했어요. ${COPY.REFRESH_APP_ADVISORY}`,
        type: AlertType.Error,
      });
    } else {
      const friendlyChatName = getFriendlyChatName(conversations[chatId]);
      const deletedByAnotherUser = userId !== store.getState().app.activeUserInfo?.id;

      store.dispatch(
        showAlertToast({
          altMessage: deletedByAnotherUser
            ? COPY.CHAT_DELETED_MESSAGE(friendlyChatName)
            : `{${friendlyChatName}} 채팅방이 삭제되었어요.`,
          altType: AlertType.Info,
        }),
      );

      if (deletedByAnotherUser)
        store.dispatch({
          type: 'conversations/disableConversation',
          payload: chatId,
        });
    }
  });

  hubConnection.on(SignalRCallbackMethods.GlobalSiteMaintenance, () => {
    store.dispatch(setMaintenance(true));
  });

  hubConnection.on(
    SignalRCallbackMethods.PluginStateChanged,
    (chatId: string, pluginName: string, pluginState: boolean) => {
      store.dispatch({
        type: 'conversations/updatePluginState',
        payload: { id: chatId, pluginName: pluginName, newState: pluginState } as UpdatePluginStatePayload,
      });
    },
  );

  hubConnection.on(SignalRCallbackMethods.ReceiveChatStatus, (status: UpdateBotStatePayload) => {
    // console.log('ReceiveChatStatus', status);
    store.dispatch({
      type: 'conversations/v2/updateBotResponseStatus',
      payload: status,
    });
  });

  hubConnection.on(SignalRCallbackMethods.ReceiveChatRoomUpdated, (chat: IChatRoom) => {
    const { id, title } = chat;
    // console.log('ReceiveChatRoomUpdated  :: title:: ', title);
    // const friendlyChatName = getFriendlyChatNameByTitle(title);

    if (id in store.getState().conversations_v2.conversations) {
      // store.dispatch(
      //     addAlert({
      //         message: `${friendlyChatName}  채팅방 이름을 변경하지 못했어요.`,
      //         type: AlertType.Error,
      //     }),
      // );
      store.dispatch({ type: 'conversations/v2/editConversationTitle', payload: { id, newTitle: title } });
    }
  });

  hubConnection.on(SignalRCallbackMethods.ReceiveChatRoomJoined, (message: string) => {
    console.log(message);
  });

  hubConnection.on(SignalRCallbackMethods.ReceiveChatRoomLeaved, (message: string) => {
    console.log(message);
  });

  hubConnection.on(SignalRCallbackMethods.ReceiveRegenerateMessageRequested, (chatId: string) => {
    store.dispatch({
      type: 'conversations/clearMessageToConversationFromServer',
      payload: { id: chatId },
    });
  });

  hubConnection.on(SignalRCallbackMethods.ReceiveUsageUpdated, (usage: IUsage) => {
    store.dispatch({
      type: 'model/updateModelUsage',
      payload: usage,
    });
  });

  hubConnection.on(
    SignalRCallbackMethods.ReceiveStreamingProgressMessage,
    (progress: IChatStreamingProgressMessage) => {
      store.dispatch({
        type: 'conversations/v2/updateBotResponseProgress',
        payload: progress,
      });
    },
  );
  hubConnection.on(
    SignalRCallbackMethods.InvokeCallTool,
    async (toolName: string, args:object) => {
      const request: IMCPRequest = {
        toolName: toolName,
        arguments: args
      };
      //console.log('mcp 요청', JSON.stringify(request), JSON.stringify(args))
      return await window.electron.requestMCP(request) as JSON;
    },
  );
};

// This is a singleton instance of the SignalR connection
let hubConnection: signalR.HubConnection | undefined = undefined;

// This function will return the singleton instance of the SignalR connection
export const getOrCreateHubConnection = (store: StoreMiddlewareAPI) => {
  if (hubConnection === undefined) {
    hubConnection = setupSignalRConnectionToChatHub();

    // Start the signalR connection to make sure messages are
    // sent to all clients and received by all clients
    startSignalRConnection(hubConnection, store);
    registerSignalREvents(hubConnection, store);
  }
  // console.log('연결상태', hubConnection.state);
  return hubConnection;
};
